import { supabase } from '../lib/supabase';
import type { GeneratedCertificate } from '../types/crm';

export type IssuedCertificateUpsert = {
  certificateNumber: string;
  customerId: string;
  attendeeName: string;
  courseName: string;
  courseDate?: string | null;
  expiryDate?: string | null;
  instructorName?: string | null;
  status?: 'active' | 'expired' | 'revoked';
  generatedAt?: string | null;
  issuedBy?: string | null;
};

function isTtNumber(value: string | undefined | null): value is string {
  return !!value && /^TT-/i.test(String(value).trim());
}

function parseSessionIdFromCertIdLocal(certId: string | undefined | null): string | undefined {
  const match = String(certId || '').match(/^session-([0-9a-f-]{36})-/i);
  return match?.[1];
}

function parseAttendeeIdFromCertIdLocal(certId: string | undefined | null): string | undefined {
  const match = String(certId || '').match(/^session-[0-9a-f-]{36}-([0-9a-f-]{36})$/i);
  return match?.[1];
}

export async function upsertIssuedCertificate(row: IssuedCertificateUpsert): Promise<void> {
  const certificateNumber = String(row.certificateNumber || '').trim();
  if (!isTtNumber(certificateNumber)) return;

  const { error } = await supabase.from('issued_certificates').upsert(
    {
      certificate_number: certificateNumber,
      customer_id: row.customerId || null,
      attendee_name: row.attendeeName || '',
      course_name: row.courseName || '',
      course_date: row.courseDate || null,
      expiry_date: row.expiryDate || null,
      instructor_name: row.instructorName || null,
      status: row.status || 'active',
      generated_at: row.generatedAt || null,
      issued_by: row.issuedBy || null,
      updated_at: new Date().toISOString(),
      ...(row.status === 'revoked'
        ? { revoked_at: new Date().toISOString() }
        : { revoked_at: null, revoked_reason: null }),
    },
    { onConflict: 'certificate_number' }
  );

  if (error) {
    console.error('Failed to upsert issued_certificates:', error);
    throw new Error(error.message || 'Could not register certificate for verification.');
  }
}

export async function upsertIssuedCertificateFromGenerated(args: {
  cert: GeneratedCertificate;
  customerId: string;
  issuedBy?: string | null;
}): Promise<void> {
  const { cert, customerId, issuedBy } = args;
  if (!isTtNumber(cert.id)) return;
  await upsertIssuedCertificate({
    certificateNumber: cert.id,
    customerId,
    attendeeName: cert.attendeeName,
    courseName: cert.courseName,
    courseDate: cert.courseDate || null,
    expiryDate: cert.expiryDate || null,
    instructorName: cert.instructorName || null,
    status: cert.status || 'active',
    generatedAt: cert.generatedAt || null,
    issuedBy: issuedBy || null,
  });
}

/** Soft-invalidate QR validation for certificates belonging to a deleted CRM participant. */
export async function revokeIssuedCertificatesForCustomer(
  customerId: string,
  reason = 'CRM participant deleted'
): Promise<number> {
  const id = String(customerId || '').trim();
  if (!id) return 0;

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('issued_certificates')
    .update({
      status: 'revoked',
      revoked_at: now,
      revoked_reason: reason,
      updated_at: now,
    })
    .eq('customer_id', id)
    .neq('status', 'revoked')
    .select('certificate_number');

  if (error) {
    console.error('Failed to revoke issued_certificates for customer:', error);
    throw new Error(error.message || 'Could not invalidate certificates for deleted participant.');
  }
  return (data || []).length;
}

/** Soft-invalidate specific certificate numbers (e.g. from generated_certificates JSON before hard delete). */
export async function revokeIssuedCertificatesByNumbers(
  certificateNumbers: string[],
  reason = 'CRM participant deleted'
): Promise<number> {
  const numbers = Array.from(
    new Set(
      (certificateNumbers || [])
        .map((n) => String(n || '').trim())
        .filter((n) => isTtNumber(n))
    )
  );
  if (numbers.length === 0) return 0;

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('issued_certificates')
    .update({
      status: 'revoked',
      revoked_at: now,
      revoked_reason: reason,
      updated_at: now,
    })
    .in('certificate_number', numbers)
    .select('certificate_number');

  if (error) {
    console.error('Failed to revoke issued_certificates by number:', error);
    throw new Error(error.message || 'Could not invalidate certificates.');
  }
  return (data || []).length;
}

function normalizeIssuedName(value: string | undefined | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function issuedCourseDateIso(value: unknown): string {
  const raw = String(value || '').trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return ymd ? `${ymd[1]}-${ymd[2]}-${ymd[3]}` : raw.slice(0, 10);
}

/** Recover a previously issued TT- number for the same person / course / date. */
export async function findIssuedCertificateNumber(args: {
  customerId?: string;
  attendeeName?: string;
  courseName?: string;
  courseDate?: string;
}): Promise<string | undefined> {
  const customerId = String(args.customerId || '').trim();
  const attendeeName = normalizeIssuedName(args.attendeeName);
  const courseName = normalizeIssuedName(args.courseName);
  const courseDate = issuedCourseDateIso(args.courseDate);
  if (!customerId || !attendeeName || !courseName) return undefined;

  const { data, error } = await supabase
    .from('issued_certificates')
    .select('certificate_number, attendee_name, course_name, course_date, generated_at, status')
    .eq('customer_id', customerId);

  if (error || !data?.length) return undefined;

  const matches = data.filter((row) => {
    if (!isTtNumber(row.certificate_number)) return false;
    if (String(row.status || '') === 'revoked') return false;
    if (normalizeIssuedName(row.attendee_name) !== attendeeName) return false;
    if (normalizeIssuedName(row.course_name) !== courseName) return false;
    if (courseDate && issuedCourseDateIso(row.course_date) !== courseDate) return false;
    return true;
  });

  if (!matches.length) return undefined;

  matches.sort((a, b) => {
    const na = Number.parseInt(String(a.certificate_number).replace(/^TT-/i, ''), 10);
    const nb = Number.parseInt(String(b.certificate_number).replace(/^TT-/i, ''), 10);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return String(a.generated_at || '').localeCompare(String(b.generated_at || ''));
  });

  return String(matches[0].certificate_number).trim();
}


export type RevokeSessionSeatArgs = {
  customerId: string;
  sessionId: string;
  attendeeId?: string | null;
  reason?: string;
};

function certBelongsToSessionSeat(
  cert: GeneratedCertificate,
  sessionId: string,
  attendeeId?: string | null
): boolean {
  const certSession =
    String(cert.sessionId || '').trim() || parseSessionIdFromCertIdLocal(cert.id) || '';
  if (!certSession || certSession !== sessionId) return false;
  const wantedAttendee = String(attendeeId || '').trim();
  if (!wantedAttendee) return true;
  const certAttendee =
    String(cert.attendeeId || '').trim() || parseAttendeeIdFromCertIdLocal(cert.id) || '';
  return !certAttendee || certAttendee === wantedAttendee;
}

/**
 * Soft-revoke certificates for a seat that left the Main Roster
 * (deleted, moved below / cancelled, no-show, or failed).
 * Marks matching generated_certificates as revoked and invalidates QR registry rows.
 * Uses supabase directly to avoid a circular import with supabaseStorage.
 */
export async function revokeCertificatesForSessionSeat(
  args: RevokeSessionSeatArgs
): Promise<{ revokedCrm: number; revokedIssued: number }> {
  const customerId = String(args.customerId || '').trim();
  const sessionId = String(args.sessionId || '').trim();
  const attendeeId = args.attendeeId ? String(args.attendeeId).trim() : '';
  const reason = String(args.reason || 'Removed from main roster').trim() || 'Removed from main roster';
  if (!customerId || !sessionId) return { revokedCrm: 0, revokedIssued: 0 };

  const { data: customerRow, error: loadErr } = await supabase
    .from('customers')
    .select('id, generated_certificates, num_certs_generated')
    .eq('id', customerId)
    .maybeSingle();
  if (loadErr) {
    console.error('revokeCertificatesForSessionSeat load failed', loadErr);
    throw new Error(loadErr.message || 'Could not load customer certificates.');
  }
  if (!customerRow) return { revokedCrm: 0, revokedIssued: 0 };

  const certs = Array.isArray(customerRow.generated_certificates)
    ? (customerRow.generated_certificates as GeneratedCertificate[])
    : [];
  const numbersToRevoke: string[] = [];
  let revokedCrm = 0;
  const next = certs.map((cert) => {
    if (cert?.status === 'revoked') return cert;
    if (!cert || !certBelongsToSessionSeat(cert, sessionId, attendeeId || null)) return cert;
    revokedCrm += 1;
    const id = String(cert.id || '').trim();
    if (id) numbersToRevoke.push(id);
    return { ...cert, status: 'revoked' as const };
  });

  if (revokedCrm > 0) {
    const activeCount = next.filter((c) => c && c.status !== 'revoked').length;
    const { error: saveErr } = await supabase
      .from('customers')
      .update({
        generated_certificates: next,
        num_certs_generated: activeCount,
        updated_at: new Date().toISOString(),
      })
      .eq('id', customerId);
    if (saveErr) {
      console.error('revokeCertificatesForSessionSeat save failed', saveErr);
      throw new Error(saveErr.message || 'Could not soft-revoke CRM certificates.');
    }
  }

  let revokedIssued = 0;
  try {
    revokedIssued = await revokeIssuedCertificatesByNumbers(numbersToRevoke, reason);
  } catch (e) {
    console.error('revokeCertificatesForSessionSeat issued revoke failed', e);
  }

  return { revokedCrm, revokedIssued };
}